import { useState } from "react";
import ArbeitstagTeil from "../arbeitsplan/ArbeitstagTeil";
import BedarfListe from "../arbeitsplan/BedarfListe";
import { tagName, type Arbeitsplan, type PlanTag } from "../../lib/arbeitsplan";
import { useUpdateTermin } from "../../hooks/usePlanung";
import KundeNeuDialog from "../kunden/KundeNeuDialog";
import KundeBlatt from "../kunden/KundeBlatt";
import NotizenBlock from "../notizen/NotizenBlock";
import { errorText } from "../../lib/invoices";
import { formatEur } from "../../lib/planer";
import type { Kunde, Termin } from "../../lib/types";
import { mitAntwort, mitKonflikten } from "../../lib/terminKonflikt";
import { KundenCombo } from "./Combos";
import { useKonfliktFrage } from "./KonfliktFrage";
import { OffeneStellenZeile, PersonList } from "./PersonList";
import { TerminFuss, TerminKopfMeta, useTerminWege } from "./TerminKopfMeta";
import { slotAenderungen, useAutoSichern } from "./terminSichern";
import { TerminFotos } from "./TerminFotos";
import { TerminOrtWahl } from "./TerminOrt";
import { TerminVorbereitung, useVorbereitungLage } from "./TerminVorbereitung";
import { Klappbereich } from "./Klappbereich";
import { useNotizen } from "../../hooks/useNotizen";
import { btnGhost, btnPrimary, fieldCap } from "./planer-styles";
import "../../styles/termin.css";
import { defaultPause, grossFor, h1, hhmm, istBesichtigung, mitEigenerZeit, mitFrischem, nettoFor, pickedCost, picksToSlots, zeitenMitziehen } from "./planer-utils";
import type { Person, Picked } from "./planer-utils";

export function EditTerminDrawer({ t, people, kunden, onClose, onTagesuebersicht, arbeitsplan, onAufnehmen }: {
  t: Termin; people: Person[]; kunden: Kunde[]; onClose: () => void; onTagesuebersicht: () => void;
  /** Im Planer: Termin schließen und am Finger halten, bis ein Zieltag angetippt
      ist — derselbe Weg wie „Verschieben" in der Tagesliste. Ohne (Arbeitsplan)
      öffnet „Verschieben" ein Datumsfeld. */
  onAufnehmen?: (modus: "verschieben" | "kopieren") => void;
  /** Der Termin ist ein Tag im Arbeitsplan des Projekts (Etappe 6): dann stehen
      „Wer führt aus" und die Leistungen dabei, und „Was muss da sein" kennt Arten. */
  arbeitsplan?: { plan: Arbeitsplan; tag: PlanTag };
}) {
  const upd = useUpdateTermin();
  const { dialog: konfliktDialog, fragen } = useKonfliktFrage();
  /* Auch aus dem offenen Termin heraus: steht der Kunde noch nicht im Stamm,
     wird er hier angelegt statt in einem zweiten Reiter. */
  const [kundeNeu, setKundeNeu] = useState<{ vorschlag: string } | null>(null);
  const [frischerKunde, setFrischerKunde] = useState<Kunde | null>(null);
  /* Kunde als Blatt von rechts — der Termin und der Planer bleiben stehen. */
  const [kundeBlatt, setKundeBlatt] = useState<string | null>(null);
  const tVon = hhmm(t.start_at) || "08:30";
  const tBis = hhmm(t.end_at) || "12:30";
  /* Das Datum ist kein Eingabefeld mehr (13.10.2026) — dafür gibt es „Verschieben".
     Nur ohne Kalender (Arbeitsplan) klappt „Verschieben" ein Datumsfeld auf. */
  const [datum, setDatum] = useState((t.start_at || "").slice(0, 10));
  const vorbereitung = useVorbereitungLage(t.id, datum);
  const notizenQ = useNotizen("offen", "alle", { termin_id: t.id });
  const termNotizen = notizenQ.data || [];
  const notizVorschau = termNotizen.length
    ? termNotizen.slice(0, 2).map((n) => n.text.trim() || n.punkte.find((pk) => !pk.erledigt_am)?.text || n.punkte[0]?.text || "").filter(Boolean).join(" · ")
    : "keine offenen";
  const [title, setTitle] = useState(t.title || "");
  const [notiz, setNotiz] = useState(t.notiz || "");
  const besichtigung = istBesichtigung(t);
  const [kundeId, setKundeId] = useState(besichtigung ? (t.kunde_id || "") : "");
  const [von, setVon] = useState(tVon);
  const [bis, setBis] = useState(tBis);
  const [offeneStellen, setOffeneStellen] = useState(t.offene_stellen || 0);
  /* Ort: "" = Standard (Baustelle, sonst Lieferadresse des Kunden). */
  const [ortQuelle, setOrtQuelle] = useState(t.einsatzort_quelle || "");
  const kundeWechseln = (id: string) => {
    // Eine Adresse des bisherigen Kunden passt nicht zum neuen.
    if (id !== kundeId && ortQuelle.startsWith("kunde:")) setOrtQuelle("");
    setKundeId(id);
  };
  const bereitsBesetzteOffeneStellen = Math.max(
    0,
    (t.offene_stellen || 0) - (t.freie_stellen ?? t.offene_stellen ?? 0),
  );
  const [picked, setPicked] = useState<Picked>(() => {
    const p: Picked = {};
    for (const sl of t.slots) {
      p[sl.person_id] = {
        slot_id: sl.id,
        von: hhmm(sl.start_at) || tVon, bis: hhmm(sl.end_at) || tBis,
        stundenlohn: sl.stundenlohn || 0, pause: sl.pause_stunden || 0, note: sl.note || "",
        confirm_status: sl.confirm_status || "geplant",
        einladungs_status: sl.einladungs_status || (sl.person_typ === "helfer" ? "wartet_auf_helfer" : "zugesagt"),
        zusatzkosten: sl.zusatzkosten || 0,
        verguetungsart: sl.verguetungsart === "pauschale" ? "pauschale" : "stundenlohn",
        pauschale_id: sl.pauschale_id ?? null,
      };
    }
    return p;
  });
  /* Terminzeit umstellen zieht die Eingeteilten mit, die bisher genau zur
     Terminzeit liefen. Wer abweicht, bleibt — siehe zeitenMitziehen. */
  const vonAendern = (v: string) => { setPicked((vorher) => zeitenMitziehen(vorher, von, bis, v, bis)); setVon(v); };
  const bisAendern = (v: string) => { setPicked((vorher) => zeitenMitziehen(vorher, von, bis, von, v)); setBis(v); };
  const eigeneZeit = mitEigenerZeit(picked, von, bis);

  /* Führt ein Packzettel den Tag aus, steht sein Helfer mit der Pauschale als
     ein Knopf über der Personenliste — einteilen mit einem Tipp. */
  const zettel = arbeitsplan ? arbeitsplan.plan.packzettel.find((z) => z.id === arbeitsplan.tag.packzettel_id) : undefined;
  const vorschlag = zettel?.helfer_id && !picked[zettel.helfer_id] ? people.find((p) => p.id === zettel.helfer_id) : undefined;
  const vorschlagEinteilen = () => {
    if (!vorschlag || !zettel) return;
    setPicked({
      ...picked,
      [vorschlag.id]: {
        von, bis, stundenlohn: vorschlag.rate, pause: defaultPause(grossFor(datum, von, bis)), note: "", confirm_status: "geplant",
        einladungs_status: "wartet_auf_helfer", zusatzkosten: 0,
        verguetungsart: zettel.pauschale_id ? "pauschale" : "stundenlohn",
        pauschale_id: zettel.pauschale_id || null,
      },
    });
  };

  const totalCost = pickedCost(picked, people, datum);
  const totalH = Object.values(picked).reduce((s, p) => s + nettoFor(datum, p.von, p.bis, p.pause), 0);

  /* Gespeichert wird automatisch (14.10.2026, terminSichern.ts). Doppelbelegung:
     das Konflikt-Blatt fragt, wie die Zeit aufgeteilt wird. */
  type Wert = { title: string; notiz: string; von: string; bis: string; datum: string; offeneStellen: number; ortQuelle: string; kundeId: string; picked: Picked };
  const wert: Wert = { title, notiz, von, bis, datum, offeneStellen, ortQuelle, kundeId, picked };
  const senden = (w: Wert, b: Wert) => {
    const helfer = Object.keys(w.picked).filter((id) => people.find((p) => p.id === id)?.role === "helfer").length;
    const patch = {
      title: w.title || (besichtigung ? "Besichtigung" : "Einsatz"),
      notiz: w.notiz,
      start_at: `${w.datum}T${w.von}:00`,
      end_at: `${w.datum}T${w.bis}:00`,
      benoetigte_helfer: besichtigung ? 0 : helfer + w.offeneStellen,
      offene_stellen: besichtigung ? 0 : w.offeneStellen,
      // Bei einem Projekttermin kommt der Kunde aus dem Projekt — nur Besichtigungen schicken ihn.
      ...(besichtigung ? { kunde_id: w.kundeId } : {}),
      // Nur wenn geaendert: der Server loest die Adresse neu auf und meldet den Ortswechsel.
      ...(w.ortQuelle !== b.ortQuelle ? { einsatzort_quelle: w.ortQuelle } : {}),
      slots: slotAenderungen(picksToSlots(w.picked, people, w.datum), picksToSlots(b.picked, people, b.datum)),
    };
    return mitKonflikten((a) => upd.mutateAsync({ id: t.id, patch: mitAntwort(patch, a) }), fragen).then((r) => !!r);
  };
  const { sichern, verwerfen, anzeige } = useAutoSichern(JSON.stringify(wert), wert, senden);
  /* Was aus diesem Termin ausgeklammert ist (Migration 206) — die Stunden
     stecken schon in der Pause der Person. */
  const luecken = (t.slots || []).flatMap((sl) => (sl.luecken || []).map((lu) => ({ name: sl.person_name, ...lu })));
  /* Eine Ebene zurück statt ganz hinaus — vorher wird ausstehendes gesichert. */
  const { weg } = useTerminWege(sichern, { zurueck: onTagesuebersicht, schliessen: onClose }, !!kundeNeu || !!konfliktDialog || !!kundeBlatt);
  const aufnehmen = onAufnehmen && ((m: "verschieben" | "kopieren") => { void sichern().then((ok) => { if (ok) onAufnehmen(m); }); });

  return (
    <>
      <div className="pl-overlay" onClick={() => weg("zurueck")}>
      <div className="pl-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {/* Kleben bleibt nur der Weg: Zurück (eine Ebene), Titel, × (ganz schließen). */}
        <div className="pl-kopf">
          <div style={{ minWidth: 0 }}>
            <button onClick={() => weg("zurueck")} className="pl-zurueck" title={arbeitsplan ? "Zurück zum Arbeitsplan" : "Termin zuklappen und den ganzen Tag zeigen"}>
              {arbeitsplan ? "‹ Arbeitsplan" : "‹ Tagesübersicht"}
            </button>
            <div className="pl-kopf-titel">
              {besichtigung ? "👁 " : ""}{t.projekt_title || t.title || "Termin"}
            </div>
          </div>
          <button type="button" onClick={() => weg("schliessen")} className="pl-closebtn" aria-label="Termin ganz schließen" title="Ganz schließen">×</button>
        </div>
        <TerminKopfMeta
          t={t}
          datum={datum}
          onDatum={setDatum}
          von={von}
          bis={bis}
          besichtigung={besichtigung}
          arbeitsplanTag={arbeitsplan ? tagName(arbeitsplan.tag) : ""}
          onAufnehmen={aufnehmen}
          onKunde={setKundeBlatt}
          onVon={vonAendern}
          onBis={bisAendern}
          eigeneZeit={eigeneZeit}
        />
        <div className="tm-rumpf">
          {arbeitsplan && (
            <div className="apl-im-termin">
              <ArbeitstagTeil plan={arbeitsplan.plan} tag={arbeitsplan.tag} />
            </div>
          )}
          <div className="tm-karte">
            {/* Der Titel sagt, was an dem Tag gemacht wird — deshalb oben und gut lesbar. */}
            <label className="tm-titel">
              <span style={fieldCap}>TITEL · SEHEN DIE HELFER</span>
              <input className="tm-feld tm-titel-feld" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Was wird an dem Tag gemacht?" />
            </label>
            {besichtigung && (
              <>
                <label style={fieldCap}>KUNDE</label>
                <div style={{ marginBottom: 10 }}>
                  <KundenCombo kunden={mitFrischem(kunden, frischerKunde)} value={kundeId} onChange={kundeWechseln} onNeu={(vorschlag) => setKundeNeu({ vorschlag })} />
                </div>
              </>
            )}
            <TerminOrtWahl
              projektId={t.projekt_id || ""}
              kundeId={besichtigung ? kundeId : (t.kunde_id || "")}
              quelle={ortQuelle}
              onQuelle={setOrtQuelle}
              gespeichert={{ quelle: t.einsatzort_quelle || "", ort: t.projekt_ort || "", name: t.einsatzort_name || "", standard: t.standard_ort || "" }}
              helfer={(t.slots || []).filter((s) => s.person_typ === "helfer").length}
            />
            {/* Kein Beschreibungsfeld mehr (14.10.2026): die Helfer lesen den Titel. Eine
                Beschreibung aus der Zeit davor wird nicht still verschluckt. */}
            {notiz.trim() && (
              <div className="tm-alt">
                <span><b>Alte Beschreibung:</b> {notiz}</span>
                <button type="button" onClick={() => { setTitle(notiz.trim()); setNotiz(""); }}>Als Titel nehmen</button>
                <button type="button" onClick={() => setNotiz("")}>Entfernen</button>
              </div>
            )}
            {!besichtigung && (
              <OffeneStellenZeile
                value={offeneStellen}
                frei={Math.max(0, (t.freie_stellen ?? t.offene_stellen ?? 0) + offeneStellen - (t.offene_stellen || 0))}
                min={bereitsBesetzteOffeneStellen}
                onChange={setOffeneStellen}
              />
            )}
            {vorschlag && zettel && (
              <button type="button" className="apl-vorschlag" onClick={vorschlagEinteilen}>
                + {vorschlag.name} einteilen
                <small>{zettel.nummer}{zettel.pauschale_id ? ` · Pauschale ${zettel.pauschale_bezeichnung || ""} ${formatEur(zettel.pauschale_betrag, { round: true })}`.trimEnd() : " · nach Stunden"}</small>
              </button>
            )}
            <PersonList people={people} datum={datum} defVon={von} defBis={bis} picked={picked} setPicked={setPicked} alwaysShowAll={false} projektId={t.projekt_id} />
            {luecken.length > 0 && (
              <div className="pl-luecken">
                {luecken.map((lu) => (
                  <div key={`${lu.name}-${lu.von}`}>
                    {lu.name.split(" ")[0]}: {hhmm(lu.von)}–{hhmm(lu.bis)} ausgeklammert (anderer Termin) — steckt in der Pause
                  </div>
                ))}
              </div>
            )}
            {/* Kein Speichern-Knopf mehr: die Leiste sagt nur, ob alles gesichert ist.
                Nur wenn das Sichern scheitert, gibt es etwas zu tun. */}
            <div className={"pl-aktionen tm-stand is-" + anzeige} role="status">
              <span style={{ fontWeight: 700, fontSize: 13 }}>{h1(totalH)} h · {formatEur(totalCost, { round: true })}</span>
              {anzeige === "fehler" ? (
                <span className="tm-stand-fehler">
                  <span>{upd.isError ? errorText(upd.error) : "Nicht gespeichert."}</span>
                  <button type="button" style={btnPrimary} onClick={() => { void sichern(); }}>Erneut versuchen</button>
                  <button type="button" style={btnGhost} onClick={() => { verwerfen(); onTagesuebersicht(); }}>Verwerfen</button>
                </span>
              ) : (
                <span className="tm-stand-text">{anzeige === "speichert" ? "Speichert …" : "✓ Gespeichert"}</span>
              )}
            </div>
          </div>
          {/* Unter dem Termin, sauber zugeklappt (13.10.2026). Zugeklappt zeigt der
              Kopf schon, was offen ist; „Was muss da sein" steht offen, solange
              etwas fehlt. */}
          {arbeitsplan ? (
            <Klappbereich titel="Was muss da sein" standardOffen>
              <BedarfListe plan={arbeitsplan.plan} tag={arbeitsplan.tag} />
            </Klappbereich>
          ) : (
            <Klappbereich
              key={vorbereitung.geladen ? "vorb" : "vorb-laedt"}
              titel="Was muss da sein"
              zahl={vorbereitung.offen}
              vorschau={vorbereitung.vorschau}
              standardOffen={vorbereitung.offen > 0}
            >
              <TerminVorbereitung terminId={t.id} datum={datum} />
            </Klappbereich>
          )}
          {/* Notizen am Termin: Kunde und Projekt ergänzt der Server aus dem Termin. */}
          <Klappbereich titel={besichtigung ? "Notizen zur Besichtigung" : "Notizen zum Termin"} zahl={termNotizen.length} vorschau={notizVorschau}>
            <NotizenBlock
              ohneKopf
              bezug={{ termin_id: t.id }}
              titel={besichtigung ? "Notizen zur Besichtigung" : "Notizen zum Termin"}
              platzhalter={besichtigung ? "Was ist zu tun? z. B. „Angebot schreiben“" : "Was ist zu tun?"}
            />
          </Klappbereich>
          <TerminFotos terminId={t.id} hatProjekt={!!t.projekt_id} />
          <TerminFuss t={t} onGeloescht={() => { verwerfen(); onTagesuebersicht(); }} onWeiterer={arbeitsplan ? undefined : () => weg("zurueck")} />
        </div>
      </div>
      </div>
      {/* Geschwister neben `.pl-overlay` — siehe DayPanel. */}
      {kundeBlatt && <KundeBlatt kundeId={kundeBlatt} onClose={() => setKundeBlatt(null)} />}
      {kundeNeu && (
        <KundeNeuDialog
          vorschlagName={kundeNeu.vorschlag}
          hinweis="landet im Kundenstamm und wird gleich in den Termin gesetzt"
          onAngelegt={(kunde) => { setFrischerKunde(kunde); kundeWechseln(kunde.id); setKundeNeu(null); }}
          onSchliessen={() => setKundeNeu(null)}
        />
      )}
      {konfliktDialog}
    </>
  );
}
